import { LogService } from '../services/log-service';

const OUNCES_PER_POUND = 16;
const GRAMS_PER_OUNCE = 28.3495;
const GRAMS_PER_KG = 1000;
const CM_PER_INCHES = 2.54;
const RETURN_ORIGINAL_VALUE = 1;

const INT_TO_UNIT_OF_MEASURE = {
  1: 'ounces',
  2: 'grams',
  3: 'inches',
  4: 'centimeters',
  5: 'pounds',
  6: 'kilograms',
  7: 'inch',
  8: 'centimeter',
};

const UNIT_OF_MEASURE_CONVERSION_MAP = {
  ounces: {
    conversionMultipliers: {
      grams: GRAMS_PER_OUNCE,
      pounds: 1 / OUNCES_PER_POUND,
      kilograms: GRAMS_PER_OUNCE / GRAMS_PER_KG,
    },
  },
  grams: {
    conversionMultipliers: {
      ounces: 1 / GRAMS_PER_OUNCE,
      pounds: 1 / (GRAMS_PER_OUNCE * OUNCES_PER_POUND),
      kilograms: 1 / GRAMS_PER_KG,
    },
  },
  pounds: {
    conversionMultipliers: {
      grams: GRAMS_PER_OUNCE * OUNCES_PER_POUND,
      ounces: OUNCES_PER_POUND,
      kilograms: (OUNCES_PER_POUND * GRAMS_PER_OUNCE) / GRAMS_PER_KG,
    },
  },
  kilograms: {
    conversionMultipliers: {
      ounces: GRAMS_PER_KG / GRAMS_PER_OUNCE,
      grams: GRAMS_PER_KG,
      pounds: GRAMS_PER_KG / (GRAMS_PER_OUNCE * OUNCES_PER_POUND),
    },
  },
  inches: {
    conversionMultipliers: {
      centimeters: CM_PER_INCHES,
    },
  },
  centimeters: {
    conversionMultipliers: {
      inches: 1 / CM_PER_INCHES,
    },
  },
  inch: {
    conversionMultipliers: {
      centimeters: CM_PER_INCHES,
      inches: RETURN_ORIGINAL_VALUE,
    },
  },
  centimeter: {
    conversionMultipliers: {
      inches: 1 / CM_PER_INCHES,
      centimeters: RETURN_ORIGINAL_VALUE,
    },
  },
};

class UnitConversionServiceClass {
  convertValue(value, currentUnitOfMeasure, desiredUnitOfMeasure) {
    if (value === null || value === undefined || currentUnitOfMeasure == null || desiredUnitOfMeasure == null) {
      return value;
    }
    if (Number.isNaN(value) || typeof value !== 'number') {
      LogService.logInfo('UnitConversionService: convertValue: Invalid value', {
        value,
        currentUnitOfMeasure,
        desiredUnitOfMeasure,
      });
      return Number.NaN;
    }
    const lowerCaseDesiredUnitOfMeasure = desiredUnitOfMeasure.toLowerCase();
    let stringCurrentUnitOfMeasure = currentUnitOfMeasure;
    if (typeof currentUnitOfMeasure === 'number') {
      stringCurrentUnitOfMeasure = INT_TO_UNIT_OF_MEASURE[currentUnitOfMeasure];
    }
    const lowerCaseCurrentUnitOfMeasure = stringCurrentUnitOfMeasure.toLowerCase();

    if (lowerCaseCurrentUnitOfMeasure === lowerCaseDesiredUnitOfMeasure) {
      return value;
    }
    const unitOfMeasureConversion = UNIT_OF_MEASURE_CONVERSION_MAP[lowerCaseCurrentUnitOfMeasure];
    if (unitOfMeasureConversion === undefined) {
      LogService.logInfo('UnitConversionService: convertValue: Invalid unit of measure provided', {
        value,
        currentUnitOfMeasure: lowerCaseCurrentUnitOfMeasure,
        desiredUnitOfMeasure: lowerCaseDesiredUnitOfMeasure,
      });
      return Number.NaN;
    }
    const conversionMultiplier = unitOfMeasureConversion.conversionMultipliers[lowerCaseDesiredUnitOfMeasure];
    if (conversionMultiplier === undefined) {
      LogService.logInfo('UnitConversionService: convertValue: Cannot convert values', {
        value,
        currentUnitOfMeasure: lowerCaseCurrentUnitOfMeasure,
        desiredUnitOfMeasure: lowerCaseDesiredUnitOfMeasure,
      });
      return Number.NaN;
    }
    return value * conversionMultiplier;
  }

  sumValues(listOfValuesAndUnitOfMeasures, desiredUnitOfMeasure) {
    if (!listOfValuesAndUnitOfMeasures || !Array.isArray(listOfValuesAndUnitOfMeasures)) {
      LogService.logInfo('UnitConversionService: sumValues: Invalid list of values', {
        listOfValuesAndUnitOfMeasures,
        desiredUnitOfMeasure,
      });
      return Number.NaN;
    }
    if (typeof desiredUnitOfMeasure !== 'string') {
      LogService.logInfo('UnitConversionService: sumValues: Invalid desired unit of measure', {
        listOfValuesAndUnitOfMeasures,
        desiredUnitOfMeasure,
      });
      return Number.NaN;
    }
    const lowerCaseDesiredUnitOfMeasure = desiredUnitOfMeasure.toLowerCase();
    return listOfValuesAndUnitOfMeasures.reduce((sum, valueAndUnitOfMeasure) => {
      if (typeof valueAndUnitOfMeasure !== 'object' || valueAndUnitOfMeasure === null) {
        return Number.NaN;
      }
      return sum + this.convertValue(valueAndUnitOfMeasure.value, valueAndUnitOfMeasure.unit, lowerCaseDesiredUnitOfMeasure);
    }, 0);
  }
}

export const UnitConversionService = new UnitConversionServiceClass();
